/*
  global
  Blob 
  Buffer
  console
  FormData
  Office
  require
  ReadableStream
  TextDecoder
  Word
*/
import * as React from "react";
import {
  Button,
  ButtonProps,
  Combobox,
  makeStyles,
  Option,
  Select,
  SelectOnChangeData,
  Textarea,
} from "@fluentui/react-components";
import type { GriffelStyle } from "@griffel/core";
import fetch from "node-fetch";
import ErrorBox from "./ErrorBox";
import PlaceholderTag from "../interfaces/PlaceholderTag";
import { LocalStorageKey as Keys, getLocalStorage, setLocalStorage } from "..";

const config = require("config");

interface HomeProps {
  accessToken: string | null;
  sessionId: string | null;
  logout: () => void;
  showLogin: () => void;
}

interface Result {
  success: boolean;
  message: string;
}

const _rawStyles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "1rem",
    margin: "auto",
  },
  h2: { margin: "1.25rem 0 0.5rem 0" },
  rootNoPad: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: 0,
    margin: 0,
  },
  col2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
  },
  errorBox: { gap: "8px" },
};
const rawStyles = _rawStyles as Record<keyof typeof _rawStyles, React.CSSProperties>;
const useStyles = makeStyles(rawStyles as Record<keyof typeof rawStyles, GriffelStyle>);

const QUESTION_LABELS: Record<string, string> = {
  q1: "Property, plant and equipment",
  q2: "Bank borrowings",
  q3: "Rental of office",
  q4: "Hire purchase",
  q5: "Directors",
  q6: "Shareholders / directors' interest",
  q7: "Investment properties",
  q8: "Investments",
  q9: "Hire purchase payables",
  q10: "Share capital",
  q11: "Investment in subsidiary",
  q12: "Investment in associate",
  q13: "Operating lease commitments",
  q14: "Intangible assets",
  q15: "Template linkage questions",
};

function formatPlaceholderLabel(name: string): string {
  const normalized = name.trim().toLowerCase();
  const match = normalized.match(/^(?:questionnaire\s*[-:]?\s*)?q((?:[1-9]|1[0-5]))(?:\b.*)?$/i);
  if (!match) {
    return name;
  }

  const qKey = `q${match[1]}`;
  const label = QUESTION_LABELS[qKey];
  return label ? `${name} (${label})` : name;
}

function normalizeSelectedTag(raw: string, tags: PlaceholderTag[]): string {
  const value = raw.trim();
  const candidates = [
    value,
    value.replace(/^[^A-Za-z0-9_]+/, "").trim(),
    value.replace(/\s+\([^)]*\)\s*$/, "").trim(),
    value
      .replace(/^[^A-Za-z0-9_]+/, "")
      .replace(/\s+\([^)]*\)\s*$/, "")
      .trim(),
  ];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    if (tags.some((t) => t.name === candidate)) {
      return candidate;
    }
  }
  return candidates[candidates.length - 1] || value;
}

const Home: React.FC<HomeProps> = (_props: HomeProps) => {
  const styles = useStyles();

  function authHeaders(extra?: Record<string, string>) {
    const headers: Record<string, string> = { ...(extra ?? {}) };
    if (_props.accessToken) {
      headers.Authorization = "Bearer " + _props.accessToken;
    }
    if (_props.sessionId) {
      headers["x-session-id"] = _props.sessionId;
    }
    return headers;
  }

  const [placeholderTags, setPlaceholderTags] = React.useState<PlaceholderTag[]>(
    getLocalStorage(Keys.Tags) ?? [
      { name: "client address", isTable: false },
      { name: "client name", isTable: false },
      { name: "date", isTable: false },
      { name: "director 1", isTable: false },
      { name: "director 2", isTable: false },
      { name: "directors", isTable: false },
      { name: "functional currency", isTable: false },
      { name: "registration no.", isTable: false },
      { name: "director's interest", isTable: true },
      { name: "property, plant and equipment", isTable: true },
      { name: "right-of-use assets", isTable: true },
      { name: "statement of cash flows", isTable: true },
      { name: "statement of changes in equity", isTable: true },
      { name: "statement of financial position", isTable: true },
      { name: "statement of profit or loss", isTable: true },
    ]
  );
  const [selectedTag, setSelectTag] = React.useState<string>(placeholderTags[0].name);
  React.useEffect(() => {
    getPlaceholderTags();
  }, [_props.accessToken]);

  const [aiPrompt, setAiPrompt] = React.useState<string>("");
  const [generating, setGenerating] = React.useState<boolean>(false);
  const [generatingError, setGeneratingError] = React.useState<string | null>(null);

  const [templates, setTemplates] = React.useState<string[]>([]);
  const [showOverwrite, setShowOverwrite] = React.useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState<string>("");
  const [saveLoading, setSaveLoading] = React.useState<boolean>(false);
  const [saveLoadingResult, setSaveLoadingResult] = React.useState<Result | null>(null);

  const [userName, setUserName] = React.useState<string | null>(null);
  React.useEffect(() => {
    fetch(config.apiUrl + "/profile/info", {
      method: "GET",
      headers: authHeaders(),
    })
      .then((res) => {
        if (res.status === 401) {
          _props.logout();
        }

        return res.json();
      })
      .then((res) => {
        if (!res.success) {
          throw Error(res.message || "Failed to fetch profile info");
        }
        setUserName(res.data.fullName);
      })
      .catch((err) => {
        console.error(err);
        setUserName(null);
      });
  }, [_props.accessToken, _props.sessionId]);

  async function getPlaceholderTags() {
    if (_props.accessToken === null) {
      return;
    }

    await fetch(config.apiUrl + "/template-placeholder", {
      method: "GET",
      headers: authHeaders(),
    })
      .then((res) => (res.status === 200 ? res.json() : null))
      .then((res) => {
        if (res?.success) {
          setPlaceholderTags(
            res.data
              .map((item: any) => ({
                name: item.name,
                isTable: item.isTable,
              }))
              .sort((a: PlaceholderTag, b: PlaceholderTag) => (a.name === b.name ? 0 : a.name > b.name ? 1 : -1))
              .sort((a: PlaceholderTag, b: PlaceholderTag) => (a.isTable === b.isTable ? 0 : a.isTable ? 1 : -1))
          );
          setLocalStorage(Keys.Tags, placeholderTags);
        }
      });
  }

  function handleTagSelect(_e: unknown, data: SelectOnChangeData) {
    setSelectTag(normalizeSelectedTag(data.value, placeholderTags));
  }

  async function handleTagOrUntag() {
    if (!selectedTag) {
      return;
    }

    await Word.run(async (context) => {
      // Check if selection is already in a content control
      try {
        const parent = context.document.getSelection().parentContentControl;
        await context.sync();

        // If inside a content control, delete it
        parent.delete(true);
        return;
      } catch {
        // eslint-disable-next-line no-empty
      }

      // Check if selection contains content controls
      const contentControls = context.document.getSelection().contentControls;
      context.load(contentControls, "items");
      await context.sync();

      // If not, wrap selection in a rich text control
      if (contentControls.items.length === 0) {
        const control = context.document.getSelection().insertContentControl(Word.ContentControlType.richText);
        control.title = selectedTag;
        control.tag = selectedTag;
        return;
      }

      // Otherwise, remove all content controls
      const selection = context.document.getSelection();
      context.load(selection, "text");
      await context.sync();

      selection.clear();
      const replacedRange = selection.insertText(selection.text, Word.InsertLocation.replace);
      replacedRange.select();
    });
  }

  function cleanString(s: string) {
    return s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  }

  async function getBeforeMidAfter(context: Word.RequestContext) {
    const selection = context.document.getSelection();
    context.load(selection, "text");
    context.load(selection, "paragraphs/items/length");
    const selectedRange = selection.getRange(Word.RangeLocation.content);
    context.load(selectedRange, "isEmpty");

    const firstP = selection.paragraphs.getFirstOrNullObject();
    const lastP = selection.paragraphs.getLastOrNullObject();
    await context.sync();

    // Special case if selection is a single paragraph
    if (!selectedRange.isEmpty && selection.paragraphs.items.length <= 1) {
      const before = firstP
        .getRange(Word.RangeLocation.start)
        .expandToOrNullObject(selectedRange.getRange(Word.RangeLocation.start));
      context.load(before, "text");
      const after = selectedRange
        .getRange(Word.RangeLocation.end)
        .expandToOrNullObject(lastP.getRange(Word.RangeLocation.end));
      context.load(after, "text");

      await context.sync();
      return {
        before: cleanString(before.text),
        mid: cleanString(selection.text ?? ""),
        after: cleanString(after.text),
      };
    }

    let befores = [];
    for (let i = 0; i < 3; i++) {
      const before = befores.length === 0 ? firstP : befores[befores.length - 1];
      befores.push(before.getPreviousOrNullObject());
      context.load(befores[befores.length - 1], "isNullObject");
      context.load(befores[befores.length - 1], "text");
    }
    befores.reverse();
    const beforeInside = firstP
      .getRange(Word.RangeLocation.start)
      .expandToOrNullObject(selectedRange.getRange(Word.RangeLocation.start));
    context.load(beforeInside, "isNullObject");
    context.load(beforeInside, "text");

    let afters = [];
    for (let i = 0; i < 3; i++) {
      const after = afters.length === 0 ? lastP : afters[afters.length - 1];
      afters.push(after.getNextOrNullObject());
      context.load(afters[afters.length - 1], "isNullObject");
      context.load(afters[afters.length - 1], "text");
    }
    const afterInside = selectedRange
      .getRange(Word.RangeLocation.end)
      .expandToOrNullObject(lastP.getRange(Word.RangeLocation.end));
    context.load(afterInside, "isNullObject");
    context.load(afterInside, "text");

    await context.sync();

    const before = [
      ...befores.filter((p) => !p.isNullObject).map((p) => p.text),
      beforeInside.isNullObject ? "" : beforeInside.text,
    ]
      .join("\n")
      .trim();
    const after = [
      afterInside.isNullObject ? "" : afterInside.text,
      ...afters.filter((p) => !p.isNullObject).map((p) => p.text),
    ]
      .join("\n")
      .trim();
    return {
      before: cleanString(before),
      mid: cleanString(selection.text ?? ""),
      after: cleanString(after),
    };
  }

  function replaceWithGeneration(context: Word.RequestContext, old: string, generation: string, done: boolean) {
    let replaced = done ? generation : generation.trimEnd();

    if (old.length > 0) {
      const startsWithCapital = old[0] === old[0].toUpperCase();
      const trimmed_start = old.slice(0, old.length - old.trimStart().length);
      const trimmed_end = old.slice(old.trimEnd().length);

      replaced = generation.trim();
      // Enforce capitalisation of first letter
      if (startsWithCapital && replaced.length > 0) {
        replaced = replaced[0].toUpperCase() + replaced.slice(1);
      } else {
        replaced = replaced[0].toLowerCase() + replaced.slice(1);
      }
      // Enforce whitespace before and after
      replaced = trimmed_start + replaced + trimmed_end;
      // If the last line is empty, it will not be selected, resulting in extra
      // trailing newlines when calling this function. Forcing the last line to
      // be non-empty fixes this.
      if (!done) {
        replaced += " ";
      }
    }

    const replacedRange = context.document.getSelection().insertText(replaced, Word.InsertLocation.replace);
    replacedRange.select();
  }

  async function handleAiGenerate() {
    if (generating || _props.accessToken === null) {
      return;
    }
    setGenerating(true);
    setGeneratingError(null);

    await Word.run(async (context) => {
      const { before, mid, after } = await getBeforeMidAfter(context);

      return fetch(config.apiUrl + "/auto-complete", {
        method: "POST",
        headers: authHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          stream: true,
          prompt: aiPrompt.trim(),
          before: before,
          mid: mid,
          after: after,
        }),
      })
        .then(async (res) => {
          if (res.status === 200) {
            return res;
          } else {
            const err = await res.json();
            throw Error(err.message);
          }
        })
        .then(async (res) => {
          const utf8Decoder = new TextDecoder("utf-8");
          const reader = (res.body as unknown as ReadableStream<Uint8Array>).getReader();
          let generation = "";
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              break;
            }

            const { data } = JSON.parse(utf8Decoder.decode(value));
            generation += data;
            replaceWithGeneration(context, mid, generation, false);
          }
          replaceWithGeneration(context, mid, generation, true);
        });
    })
      .catch((err) => {
        console.error(err);
        setGeneratingError(err.message);
      })
      .finally(() => setGenerating(false));
  }

  async function getTemplates() {
    if (_props.accessToken === null) {
      return;
    }

    await fetch(config.apiUrl + "/template", {
      method: "GET",
      headers: authHeaders(),
    })
      .then((res) => {
        if (res.status !== 200) {
          throw Error("Failed to fetch templates");
        }
        return res.json();
      })
      .then((res) => {
        if (!res.success) {
          throw Error(res.message || "Failed to fetch templates");
        }
        setTemplates(res.data.sort());
      })
      .catch((err) => {
        console.error(err);
      });
  }

  function getFileAsync(): Promise<Office.File> {
    return new Promise((resolve, reject) => {
      try {
        Office.context.document.getFileAsync(Office.FileType.Compressed, (result) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            return reject(result.error);
          }
          return resolve(result.value);
        });
      } catch (err) {
        return reject(err);
      }
    });
  }

  function getSliceAsync(file: Office.File, i: number): Promise<Office.Slice> {
    return new Promise((resolve, reject) => {
      try {
        file.getSliceAsync(i, (slice) => {
          if (slice.status !== Office.AsyncResultStatus.Succeeded) {
            return reject(slice.error);
          }
          return resolve(slice.value);
        });
      } catch (err) {
        return reject(err);
      }
    });
  }

  async function handleSaveTemplate(force: boolean) {
    if (saveLoading || !selectedTemplate) {
      return;
    }
    setSaveLoading(true);
    setSaveLoadingResult(null);
    setShowOverwrite(false);

    await getFileAsync()
      .then(async (file) => {
        let bytes = new Uint8Array(file.size);
        let offset = 0;
        for (let i = 0; i < file.sliceCount; i++) {
          await getSliceAsync(file, i).then((slice) => {
            const data = slice.data as Uint8Array;
            bytes.set(data, offset);
            offset += data.length;
          });
        }
        file.closeAsync();

        const form = new FormData();
        form.append("file", new Blob([bytes], { type: "application/octet-stream" }), selectedTemplate);

        await fetch(`${config.apiUrl}/template/${selectedTemplate}`, {
          method: "POST",
          headers: authHeaders({
            Force: force ? "1" : "0",
          }),
          body: form as any,
        }).then(async (res) => {
          if (res.status === 409) {
            const err = await res.json();
            setShowOverwrite(true);
            throw Error(err.message || "Template already exists");
          }
          if (res.status !== 201) {
            throw Error("Failed to save template");
          }
        });
      })
      .then(() => setSaveLoadingResult({ success: true, message: `Saved ${selectedTemplate}.docx` }))
      .catch((err) => {
        console.error(err);
        setSaveLoadingResult({ success: false, message: err.message });
      })
      .finally(() => setSaveLoading(false));
  }

  async function handleLoadTemplate() {
    if (saveLoading || !selectedTemplate) {
      return;
    }
    setSaveLoading(true);
    setSaveLoadingResult(null);
    setShowOverwrite(false);

    await fetch(`${config.apiUrl}/template/${selectedTemplate}`, {
      method: "GET",
      headers: authHeaders(),
    })
      .then(async (res) => {
        if (res.status !== 200) {
          const err = await res.json();
          throw Error(err.message || "Failed to fetch template");
        }
        return res.arrayBuffer();
      })
      .then((buf) =>
        Word.run(async (context) => {
          const template = context.application.createDocument(Buffer.from(buf).toString("base64"));
          template.open();
          await context.sync();
        })
      )
      .catch((err) => {
        console.error(err);
        setSaveLoadingResult({ success: false, message: err.message });
      })
      .finally(() => setSaveLoading(false));
  }

  function loginProtectedButton(props: ButtonProps, text: string) {
    if (_props.accessToken) {
      return <Button {...props}>{text}</Button>;
    } else {
      return (
        <Button disabled title="You must log in to use this feature">
          {text}
        </Button>
      );
    }
  }

  function resultBox(result: Result | null, setResult: (result: any | null) => void) {
    if (result === null) {
      return null;
    }
    return (
      <ErrorBox
        show
        extraStyles={{
          ...rawStyles.errorBox,
          backgroundColor: result.success ? "darkgreen" : "darkred",
          borderColor: result.success ? "green" : "red",
        }}
      >
        <div>
          {result.success || "Error: "}
          {result.message}
        </div>
        <Button onClick={() => setResult(null)}>Close</Button>
      </ErrorBox>
    );
  }

  return (
    <div className={styles.root}>
      <h2 className={styles.h2} style={{ marginTop: "0" }}>
        Placeholder Tag
      </h2>
      <Select defaultValue={selectedTag} onChange={handleTagSelect}>
        {placeholderTags.map((option) => (
          <option key={option.name} value={option.name}>
            {option.isTable ? "📅" : "💬"} {formatPlaceholderLabel(option.name)}
          </option>
        ))}
      </Select>
      <Button onClick={handleTagOrUntag}>Tag/Untag selected area</Button>

      <h2 className={styles.h2}>AI Writer</h2>
      <span>
        <div style={{ margin: "0.35rem 0", marginTop: 0 }}>
          To paraphrase <b>existing</b> text, <b>select</b> the text to be paraphrased.
        </div>
        <div style={{ margin: "0.35rem 0" }}>
          To insert <b>new</b> text, <b>place</b> the cursor where the text should be inserted.
        </div>
      </span>
      <Textarea
        disabled={_props.accessToken === null}
        rows={5}
        defaultValue={aiPrompt}
        placeholder="Give the AI special instructions (optional)"
        onChange={(event) => {
          setAiPrompt(event.target.value);
        }}
      />
      {loginProtectedButton({ disabled: generating, onClick: handleAiGenerate }, "Generate")}
      {resultBox(generatingError ? { success: false, message: generatingError } : null, setGeneratingError)}

      <h2 className={styles.h2}>Save Template</h2>
      <form className={styles.rootNoPad}>
        <Combobox
          disabled={_props.accessToken === null}
          required
          defaultValue={selectedTemplate}
          placeholder="Template Name"
          freeform
          onFocus={getTemplates}
          onChange={(e) => setSelectedTemplate(e.target.value)}
          onOptionSelect={(_, data) => setSelectedTemplate(data.optionValue)}
        >
          {templates.map((option) => (
            <Option key={option}>{option}</Option>
          ))}
        </Combobox>
        <div className={styles.col2}>
          {loginProtectedButton(
            {
              type: "submit",
              disabled: saveLoading,
              onClick: () => handleSaveTemplate(false),
            },
            "Save"
          )}
          {loginProtectedButton(
            {
              type: "submit",
              disabled: saveLoading,
              onClick: handleLoadTemplate,
            },
            "Load"
          )}
        </div>
        {showOverwrite ? (
          <ErrorBox show extraStyles={rawStyles.errorBox}>
            <div>
              {"Error: "}
              {saveLoadingResult.message}
            </div>
            <div className={styles.col2}>
              <Button
                onClick={() => {
                  setSaveLoadingResult(null);
                  setShowOverwrite(false);
                }}
              >
                No
              </Button>
              <Button onClick={() => handleSaveTemplate(true)}>Yes</Button>
            </div>
          </ErrorBox>
        ) : (
          resultBox(saveLoadingResult, setSaveLoadingResult)
        )}
      </form>

      {_props.accessToken ? (
        <>
          <h2 className={styles.h2}>Log out</h2>
          {userName === null ? (
            <span>Unable to fetch username. Check your internet connection.</span>
          ) : (
            <span>
              You are logged in as <b>{userName}</b>
            </span>
          )}
          <Button onClick={_props.logout}>Log out</Button>
        </>
      ) : (
        <ErrorBox show extraStyles={rawStyles.errorBox}>
          <div>You will not be able to use certain features unless you log in.</div>
          <Button onClick={_props.showLogin}>Login</Button>
        </ErrorBox>
      )}
    </div>
  );
};

export default Home;
